import type { z } from "zod";

/**
 * A registered behavior definition: a code-reviewed contract the view picks
 * by id and version. Everything a definition declares is validated: the plan
 * is compiled against it, the browser host runs it, and the authoring
 * reference is generated from it.
 *
 * Definitions hold Zod schemas and metadata only, so this module stays
 * server-safe.
 */

export interface BehaviorServiceSlot {
  /** What the operation sends the bound command, by `{ "$param" }` name. */
  readonly params: readonly string[];
  /** One line for the authoring reference. */
  readonly description: string;
}

export interface BehaviorOperationDefinition {
  readonly description: string;
  /** Runtime-validated input. A resolved `$state` reference proves nothing by itself. */
  readonly input: z.ZodType;
  /** Events the operation emits after it succeeds, with their payload schemas. */
  readonly emits: Readonly<Record<string, z.ZodType>>;
  /** Service slots the operation needs a command bound to. */
  readonly services: Readonly<Record<string, BehaviorServiceSlot>>;
  /** True when the operation changes the instance's own state. */
  readonly mutates: boolean;
}

export interface BehaviorDefinition {
  readonly id: string;
  readonly version: number;
  readonly title: string;
  readonly description: string;
  /** The instance's initial own state. */
  readonly state: z.ZodType<Record<string, unknown>>;
  /** Per-instance configuration. */
  readonly config: z.ZodType<Record<string, unknown>>;
  readonly operations: Readonly<Record<string, BehaviorOperationDefinition>>;
  /** Events that must have an explicit policy when the instance is reachable. */
  readonly publicEvents: readonly string[];
}

/** Declare a definition; the type keeps every registry entry shaped the same. */
export function defineBehavior(
  definition: BehaviorDefinition,
): BehaviorDefinition {
  return definition;
}

/** A registered payload adapter: names and validates one payload mapping. */
export interface BehaviorPayloadAdapter {
  readonly id: string;
  readonly description: string;
  /** The shape the adapter accepts after the plan's template is resolved. */
  readonly input: z.ZodType;
}
